import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Navigation } from '../Navigation';
import { canAccessSymbol } from '../../lib/entitlements';
import '../Dashboard.css';

const API = process.env.REACT_APP_API_URL || 'https://4jbhzumjk7.execute-api.us-east-2.amazonaws.com/prod';

const c = {
  bg: '#0f172a', card: '#1f2937', cardAlt: '#1a2332', border: '#374151',
  text: '#f9fafb', muted: '#9ca3af', dim: '#6b7280',
  green: '#16a34a', greenBg: '#16a34a20', blue: '#3b82f6', blueBg: '#3b82f620',
  amber: '#d97706', amberBg: '#d9770620', red: '#dc2626', purple: '#8b5cf6',
};

const fmt = (n: any, d = 2) => (n == null ? '—' : Number(n).toFixed(d));
const fmtExp = (s: string) => { try { return new Date(s + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); } catch { return s || '—'; } };
const fmtDate = (s: string) => { try { return new Date(s).toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); } catch { return s || '—'; } };

const badge = (color: string, bg: string): React.CSSProperties => ({
  display: 'inline-block', padding: '0.15rem 0.5rem', borderRadius: 12,
  fontSize: '0.7rem', fontWeight: 600, background: bg, color,
});

const HORIZON: Record<number, { label: string; desc: string; color: string }> = {
  0: { label: '0 DTE', desc: 'Intraday', color: '#ef4444' },
  1: { label: '1 DTE', desc: 'Overnight', color: '#f97316' },
  7: { label: '7 DTE', desc: 'Weekly', color: '#f59e0b' },
  14: { label: '14 DTE', desc: 'Bi-weekly', color: '#84cc16' },
  21: { label: '21 DTE', desc: '3-week', color: '#22c55e' },
  30: { label: '30 DTE', desc: 'Monthly', color: '#10b981' },
  45: { label: '45 DTE', desc: 'Standard', color: '#06b6d4' },
  60: { label: '60 DTE', desc: 'Long-term', color: '#3b82f6' },
  90: { label: '90 DTE', desc: '3-month', color: '#8b5cf6' },
  120: { label: '120 DTE', desc: '4-month', color: '#a855f7' },
};

const skewCfg: Record<string, { color: string; bg: string; label: string; desc: string }> = {
  normal:   { color: c.green, bg: c.greenBg, label: 'BALANCED',   desc: 'Downside protection demand is normal \u2014 investors aren\u2019t paying extra for crash insurance. The puts you sell are priced fairly.' },
  cautious: { color: c.amber, bg: c.amberBg, label: 'CAUTIOUS',   desc: 'Investors are starting to buy more crash insurance, which increases the premiums you collect. However, it also signals some uncertainty \u2014 stick with safer strikes further from the current price.' },
  elevated: { color: '#f97316', bg: '#f9731620', label: 'ELEVATED', desc: 'Significant demand for crash protection \u2014 investors are worried about a sharp decline. Higher premiums but higher risk. Our scores favor more conservative trades.' },
  extreme:  { color: c.red,   bg: '#dc262620', label: 'EXTREME',   desc: 'Heavy crash insurance buying \u2014 investors are paying a lot for protection. Premiums are very high but so is the risk. Consider sitting out or using minimal position sizes.' },
};

// Combined VIX + Skew market conditions signal
function getTradingSignal(vix: number | null, skewRegime: string): { label: string; color: string; bg: string; desc: string } {
  const isHighVix = vix != null && vix >= 30;
  const isMidVix = vix != null && vix >= 20;

  if (isHighVix && (skewRegime === 'elevated' || skewRegime === 'extreme')) {
    return { label: 'HIGH ALERT', color: c.red, bg: '#dc262620', desc: 'Both overall volatility and crash protection demand are extreme. Market is pricing in significant risk in both directions.' };
  }
  if (isHighVix) {
    return { label: 'HIGH VOL / SKEW BALANCED', color: '#f97316', bg: '#f9731620', desc: 'Overall volatility is high but crash protection demand has normalized. Large moves are expected but not specifically to the downside. Premiums are elevated.' };
  }
  if (isMidVix && (skewRegime === 'elevated' || skewRegime === 'extreme')) {
    return { label: 'DEFENSIVE', color: c.amber, bg: c.amberBg, desc: 'Above-normal volatility combined with rising crash protection demand. Market participants are positioning for potential sharp downside moves.' };
  }
  if (isMidVix) {
    return { label: 'ELEVATED VOL', color: c.blue, bg: c.blueBg, desc: 'Volatility is above normal but options pricing is balanced between puts and calls. No strong directional bias in the options market.' };
  }
  if (skewRegime === 'elevated' || skewRegime === 'extreme') {
    return { label: 'HIDDEN RISK', color: '#f97316', bg: '#f9731620', desc: 'Overall volatility appears low but crash protection demand is elevated. Institutional participants may be hedging against a potential sharp move.' };
  }
  return { label: 'BALANCED', color: c.green, bg: c.greenBg, desc: 'Low overall volatility with balanced options pricing. No unusual demand for crash protection.' };
}

// ── Convergence display helpers (score computed on backend) ─────────────────

function convergenceStyle(conv: any) {
  if (!conv) return null;
  const s = conv.score;
  if (s >= 75) return { ...conv, color: '#16a34a', bg: '#16a34a20' };
  if (s >= 40) return { ...conv, color: '#d97706', bg: '#d9770620' };
  return { ...conv, color: '#6366f1', bg: '#6366f120' };
}

const ConvergenceHelp: React.FC<{ color: string }> = ({ color }) => {
  const [open, setOpen] = React.useState(false);
  return (
    <div style={{ marginBottom: '0.75rem' }}>
      <span onClick={() => setOpen(!open)} style={{ fontSize: '0.72rem', color, cursor: 'pointer', userSelect: 'none' }}>
        {open ? '\u25BC' : '\u25B6'} How to read Convergence Score
      </span>
      {open && (
        <div style={{ fontSize: '0.78rem', color: c.muted, lineHeight: 1.7, marginTop: '0.4rem', padding: '0.6rem 0.85rem', background: c.cardAlt, borderRadius: 6, border: `1px solid ${c.border}` }}>
          <p style={{ margin: '0 0 0.5rem' }}>Three independent systems evaluate every horizon: our quantitative model, Claude, and ChatGPT. The AIs never see each other's output or the model's score.</p>
          <p style={{ margin: '0 0 0.3rem' }}><span style={{ color: '#16a34a', fontWeight: 700 }}>STRONG</span> — All three pick the same strike. The strongest reading we produce. When systems that evaluate risk differently all point to the same trade, the selection is more likely robust.</p>
          <p style={{ margin: '0 0 0.3rem' }}><span style={{ color: '#d97706', fontWeight: 700 }}>MEDIUM</span> — Two of three agree. Look at <em>who</em> agrees: "Both AIs agree" means two independent AI systems converged (strong qualitative reading). "Model + one AI" means quantitative and one qualitative align.</p>
          <p style={{ margin: '0 0 0.3rem' }}><span style={{ color: '#6366f1', fontWeight: 700 }}>MIXED</span> — Each analysis picks a different strike. The market is ambiguous at this horizon. If you trade, consider sizing smaller or selecting based on the individual trade metrics.</p>
          <p style={{ margin: '0', fontStyle: 'italic' }}>Convergence measures agreement under uncertainty — not profit prediction. It tells you how confident the system is in its selection, not how much the trade will make.</p>
        </div>
      )}
    </div>
  );
};

// ── Skew Card (matching V1) ─────────────────────────────────────────────────

export const SkewCard: React.FC<{ skew: any; prevSkew?: any }> = ({ skew, prevSkew }) => {
  const cfg = skewCfg[skew?.skew_regime] || skewCfg.normal;
  const expirations = skew?.skew_by_expiration ? Object.entries(skew.skew_by_expiration) : [];
  const [showDetail, setShowDetail] = useState(false);
  const vix = skew?.vix;
  const tradingSignal = getTradingSignal(vix, skew?.skew_regime);

  // Plain-English "why it changed" — no raw decimals shown to subscribers.
  const getChangeExplanation = () => {
    const ratio = skew?.skew_ratio;
    const prevRatio = prevSkew?.skew_ratio;
    const prevRegime = prevSkew?.skew_regime;
    const regime = skew?.skew_regime;

    if (!prevRatio || !ratio) return null;
    if (regime === prevRegime && Math.abs(ratio - prevRatio) < 0.005) return null;

    const diff = ratio - prevRatio;
    if (regime === prevRegime) {
      return diff > 0
        ? 'Crash-protection demand edged up slightly but remains in the same range. No change to our assessment.'
        : 'Crash-protection demand eased slightly but remains in the same range. No change to our assessment.';
    }
    const prevLabel = skewCfg[prevRegime]?.label || prevRegime;
    const newLabel = cfg.label;
    if (diff > 0) {
      return `Investors are buying more downside protection. Mood shifted from ${prevLabel} to ${newLabel} — today's read is more cautious.`;
    }
    return `Demand for downside protection eased. Mood improved from ${prevLabel} to ${newLabel} — today's read is friendlier.`;
  };

  const changeExplanation = getChangeExplanation();

  // Plain-English impact descriptor (replaces raw skew_penalty decimal)
  const skewImpactLabel = (() => {
    const p = skew?.skew_penalty;
    if (p == null || p === 0) return 'No impact on today’s trades';
    const abs = Math.abs(p);
    if (abs <= 0.05) return 'Small caution on today’s trades';
    if (abs <= 0.12) return 'Moderate caution on today’s trades';
    return 'Heavy caution on today’s trades';
  })();

  return (
    <div style={{
      borderRadius: 8, border: `2px solid ${tradingSignal.color}`, padding: '1rem', marginBottom: '1rem',
      background: `linear-gradient(135deg, ${c.card} 0%, ${tradingSignal.bg} 100%)`,
    }}>
      {/* Combined Trading Signal — top banner */}
      <div style={{
        background: `${tradingSignal.color}15`, border: `1px solid ${tradingSignal.color}40`, borderRadius: 6,
        padding: '0.6rem 0.75rem', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.75rem',
      }}>
        <span style={{ ...badge(tradingSignal.color, tradingSignal.bg), fontSize: '0.75rem', padding: '0.2rem 0.6rem' }}>
          {tradingSignal.label}
        </span>
        <span style={{ fontSize: '0.82rem', color: c.muted, lineHeight: 1.4 }}>{tradingSignal.desc}</span>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <h3 style={{ margin: 0, fontSize: '1rem' }}>Crash Protection Demand</h3>
          <span style={badge(cfg.color, cfg.bg)}>{cfg.label}</span>
        </div>
        <span style={{ fontSize: '0.75rem', color: c.muted }}>
          {skew.expirations_measured} expirations measured
        </span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem', marginBottom: '0.75rem' }}>
        {vix != null && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '0.65rem', color: c.muted }}>VIX</div>
            <div style={{ fontSize: '1.3rem', fontWeight: 700, color: vix >= 30 ? c.red : vix >= 20 ? c.amber : c.green }}>{vix}</div>
            <div style={{ fontSize: '0.6rem', color: c.dim }}>{vix >= 30 ? 'High Fear' : vix >= 20 ? 'Choppy' : 'Tradeable'}</div>
          </div>
        )}
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '0.65rem', color: c.muted }}>Crash-protection demand</div>
          <div style={{ fontSize: '1.1rem', fontWeight: 700, color: cfg.color, marginTop: '0.15rem' }}>{cfg.label}</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '0.65rem', color: c.muted }}>Impact on today's trades</div>
          <div style={{ fontSize: '0.95rem', fontWeight: 700, color: skew.skew_penalty < 0 ? c.red : c.green, marginTop: '0.15rem' }}>
            {skewImpactLabel}
          </div>
        </div>
      </div>

      {changeExplanation && (
        <div style={{
          background: `${cfg.color}15`, border: `1px solid ${cfg.color}40`, borderRadius: 6,
          padding: '0.6rem 0.75rem', marginBottom: '0.5rem',
        }}>
          <div style={{ fontSize: '0.7rem', fontWeight: 700, color: cfg.color, marginBottom: '0.25rem', textTransform: 'uppercase' }}>
            Why it changed
          </div>
          <p style={{ margin: 0, fontSize: '0.8rem', color: c.muted, lineHeight: 1.5 }}>{changeExplanation}</p>
        </div>
      )}

      <p style={{ margin: '0 0 0.5rem', fontSize: '0.82rem', color: c.muted, lineHeight: 1.5 }}>{cfg.desc}</p>
      <p style={{ margin: 0, fontSize: '0.72rem', color: c.dim, fontStyle: 'italic' }}>
        <strong>How to read this:</strong> VIX measures overall market volatility (how big moves are expected). Put/Call Skew measures directional crash fear (are investors buying crash insurance?). They can disagree &mdash; high VIX with balanced skew means big moves are expected but not specifically downward. The reading above combines both into one assessment.
      </p>

      {expirations.length > 0 && (
        <div style={{ marginTop: '0.5rem' }}>
          <button
            onClick={() => setShowDetail(!showDetail)}
            style={{ background: 'transparent', border: 'none', color: c.blue, cursor: 'pointer', fontSize: '0.78rem', padding: 0 }}
          >
            {showDetail ? 'Hide' : 'Show'} per-expiration breakdown
          </button>
          {showDetail && (
            <div style={{ overflowX: 'auto', marginTop: '0.5rem' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                <thead>
                  <tr>
                    {['Expires', 'Downside Protection Cost', 'Upside Cost', 'Fear Ratio', 'Put Strike', 'Call Strike'].map(h => (
                      <th key={h} style={{ padding: '0.5rem 0.4rem', textAlign: 'left', borderBottom: `1px solid ${c.border}`, color: c.muted, fontWeight: 600, fontSize: '0.72rem' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {expirations.sort((a: any, b: any) => a[0].localeCompare(b[0])).map(([exp, data]: [string, any]) => (
                    <tr key={exp}>
                      <td style={{ padding: '0.45rem 0.4rem', borderBottom: `1px solid ${c.border}22` }}>{fmtExp(exp)}</td>
                      <td style={{ padding: '0.45rem 0.4rem', borderBottom: `1px solid ${c.border}22` }}>{data.put_iv?.toFixed(1)}%</td>
                      <td style={{ padding: '0.45rem 0.4rem', borderBottom: `1px solid ${c.border}22` }}>{data.call_iv?.toFixed(1)}%</td>
                      <td style={{ padding: '0.45rem 0.4rem', borderBottom: `1px solid ${c.border}22`, fontWeight: 600, color: data.skew_ratio >= 1.25 ? c.amber : c.text }}>
                        {data.skew_ratio?.toFixed(4)}
                      </td>
                      <td style={{ padding: '0.45rem 0.4rem', borderBottom: `1px solid ${c.border}22` }}>{data.put_strike}</td>
                      <td style={{ padding: '0.45rem 0.4rem', borderBottom: `1px solid ${c.border}22` }}>{data.call_strike}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Trade Card (reusable for Our Pick, Claude, ChatGPT) ────────────────────

const metrics = (t: any) => [
  { label: 'Strike Price', value: String(t.strike || '—') },
  { label: 'Expires On', value: fmtExp(t.expiration) },
  { label: 'Days Left', value: String(t.dte ?? t.actual_dte ?? '—') },
  { label: 'You Collect', value: `$${fmt(t.credit)}` },
  { label: 'Daily Credit', value: t.credit_per_day != null ? `$${fmt(t.credit_per_day, 3)}` : '—' },
  { label: 'Risk Level', value: fmt(t.delta, 4) },
  { label: 'Safety Buffer', value: t.points_from_atm != null ? fmt(t.points_from_atm, 1) : '—' },
  { label: 'Win Chance', value: `${fmt(t.prob_profit_pct, 1)}%` },
  { label: 'Reward/Risk', value: fmt(t.sharpe_annualized) },
  { label: 'Score', value: fmt(t.score_v2 ?? t.score_enhanced, 3) },
  { label: 'Volatility', value: t.iv_pct != null ? `${fmt(t.iv_pct, 1)}%` : '—' },
  { label: 'Gamma Adj', value: fmt(t.gamma_adjustment, 2) },
];

const TradeCard: React.FC<{ title: string; badgeText: string; accentColor: string; trade: any; reasoning?: string }> = ({
  title, badgeText, accentColor, trade, reasoning,
}) => {
  if (!trade || !trade.strike) return null;
  return (
    <div style={{
      background: `linear-gradient(135deg, ${c.card} 0%, ${accentColor}10 100%)`,
      borderRadius: 8, border: `2px solid ${accentColor}`, padding: '1.25rem', marginBottom: '0.75rem',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
        <span style={{ fontSize: '1.3rem' }}>&#9733;</span>
        <h3 style={{ margin: 0, fontSize: '1rem' }}>{title}</h3>
        <span style={badge(accentColor, accentColor + '20')}>{badgeText}</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(90px, 1fr))', gap: '0.6rem', marginBottom: '0.75rem' }}>
        {metrics(trade).map(m => (
          <div key={m.label} style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '0.6rem', color: c.muted, marginBottom: '0.1rem' }}>{m.label}</div>
            <div style={{ fontSize: '0.88rem', fontWeight: 700 }}>{m.value}</div>
          </div>
        ))}
      </div>
      {reasoning && (
        <div style={{ background: c.cardAlt, borderRadius: 6, padding: '0.75rem', border: `1px solid ${c.border}` }}>
          <div style={{ fontSize: '0.7rem', color: c.muted, fontWeight: 600, marginBottom: '0.25rem' }}>
            {title.includes('Our') ? 'Why We Recommend This' : 'Why AI Chose This'}
          </div>
          <p style={{ margin: 0, fontSize: '0.85rem', lineHeight: 1.6, whiteSpace: 'pre-line' }}>{reasoning}</p>
        </div>
      )}
    </div>
  );
};

// ── Chat Panel ──────────────────────────────────────────────────────────────

// Exported so Notches (and any future signal product) can reuse it. Pass an
// `endpoint` like '/spx/v2-chat' or '/notches/chat' and any extra fields the
// backend expects via `extraBody` (e.g. {symbol: 'SPX'} for Notches).
export const ChatSidebar: React.FC<{
  signal: any;
  token: string;
  open: boolean;
  onClose: () => void;
  endpoint?: string;
  examples?: string[];
  extraBody?: Record<string, any>;
}> = ({
  signal,
  token,
  open,
  onClose,
  endpoint = '/spx/v2-chat',
  examples = ['Compare 30-day and 45-day trades', 'Why is the 7-day pick risky?', 'Which horizon has the best value?'],
  extraBody = {},
}) => {
  const [messages, setMessages] = useState<Array<{ role: 'user' | 'ai'; text: string; cached?: boolean }>>([]);
  const [input, setInput] = useState('');
  const [asking, setAsking] = useState(false);
  const messagesEndRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleAsk = async () => {
    if (!input.trim() || asking) return;
    const question = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: question }]);
    setAsking(true);
    try {
      const resp = await fetch(`${API}${endpoint}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ question, signalDate: signal?.date, snapshotTime: signal?.snapshot_time, ...extraBody }),
      });
      const data = await resp.json();
      setMessages(prev => [...prev, { role: 'ai', text: data.answer || data.error || 'Could not process that question.', cached: data.cached }]);
    } catch {
      setMessages(prev => [...prev, { role: 'ai', text: 'Failed to connect. Please try again.' }]);
    } finally { setAsking(false); }
  };

  return (
    <>
      {/* Overlay */}
      {open && <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 250 }} />}

      {/* Panel */}
      <div style={{
        position: 'fixed', top: 52, right: 0, bottom: 0, width: 380,
        background: c.card, borderLeft: `1px solid ${c.border}`,
        zIndex: 260, display: 'flex', flexDirection: 'column',
        transform: open ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.25s ease',
        boxShadow: open ? '-4px 0 16px rgba(0,0,0,0.3)' : 'none',
      }}>
        {/* Header */}
        <div style={{
          padding: '0.75rem 1rem', borderBottom: `1px solid ${c.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '1rem' }}>&#128172;</span>
            <h4 style={{ margin: 0, fontSize: '0.9rem' }}>Ask About Results</h4>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', color: c.muted, cursor: 'pointer',
            fontSize: '1.2rem', padding: '0.25rem',
          }}>&times;</button>
        </div>

        {/* Messages */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '0.75rem' }}>
          {messages.length === 0 && (
            <div style={{ textAlign: 'center', padding: '2rem 1rem', color: c.dim }}>
              <p style={{ fontSize: '0.85rem', lineHeight: 1.6, margin: '0 0 1rem' }}>
                Ask questions about the research results. For example:
              </p>
              {examples.map(q => (
                <button key={q} onClick={() => { setInput(q); }} style={{
                  display: 'block', width: '100%', textAlign: 'left', padding: '0.5rem 0.75rem',
                  marginBottom: '0.35rem', borderRadius: 6, border: `1px solid ${c.border}`,
                  background: c.cardAlt, color: c.muted, cursor: 'pointer', fontSize: '0.78rem',
                }}>{q}</button>
              ))}
            </div>
          )}
          {messages.map((msg, i) => (
            <div key={i} style={{
              marginBottom: '0.6rem', display: 'flex', flexDirection: 'column',
              alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start',
            }}>
              <div style={{
                maxWidth: '90%', padding: '0.5rem 0.75rem',
                borderRadius: msg.role === 'user' ? '10px 10px 2px 10px' : '10px 10px 10px 2px',
                background: msg.role === 'user' ? c.purple + '25' : c.cardAlt,
                border: `1px solid ${msg.role === 'user' ? c.purple + '35' : c.border}`,
              }}>
                <div style={{ fontSize: '0.55rem', color: c.dim, marginBottom: '0.15rem', fontWeight: 600 }}>
                  {msg.role === 'user' ? 'You' : 'AI'}
                  {msg.cached && <span style={{ marginLeft: '0.4rem', color: c.green }}>cached</span>}
                </div>
                <div style={{ fontSize: '0.8rem', lineHeight: 1.6, whiteSpace: 'pre-line' }}>{msg.text}</div>
              </div>
            </div>
          ))}
          {asking && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: c.muted, fontSize: '0.78rem', padding: '0.5rem' }}>
              <span className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} /> Thinking...
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div style={{ padding: '0.6rem', borderTop: `1px solid ${c.border}`, display: 'flex', gap: '0.4rem' }}>
          <input
            type="text" value={input} onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleAsk(); }}
            placeholder="Ask a question..."
            disabled={asking}
            style={{
              flex: 1, padding: '0.5rem 0.6rem', borderRadius: 6,
              border: `1px solid ${c.border}`, background: c.cardAlt, color: c.text,
              fontSize: '0.8rem', outline: 'none',
            }}
          />
          <button onClick={handleAsk} disabled={asking || !input.trim()} style={{
            padding: '0.5rem 0.75rem', borderRadius: 6, border: 'none',
            background: c.purple, color: '#fff', fontWeight: 600, fontSize: '0.8rem',
            cursor: asking ? 'wait' : 'pointer', opacity: asking || !input.trim() ? 0.5 : 1,
          }}>Ask</button>
        </div>
      </div>
    </>
  );
};

// ── Market Pulse Modal ──────────────────────────────────────────────────────

const GaugeArc: React.FC<{ value: number; min: number; max: number; zones: Array<{ from: number; to: number; color: string }>; label: string; unit?: string }> = ({ value, min, max, zones, label, unit = '' }) => {
  const pct = Math.max(0, Math.min(1, (value - min) / (max - min)));
  const angle = -90 + pct * 180;
  return (
    <div style={{ textAlign: 'center' }}>
      <svg viewBox="0 0 200 120" width="180" height="110">
        {zones.map((z, i) => {
          const startAngle = -90 + ((z.from - min) / (max - min)) * 180;
          const endAngle = -90 + ((z.to - min) / (max - min)) * 180;
          const r = 80;
          const x1 = 100 + r * Math.cos((startAngle * Math.PI) / 180);
          const y1 = 100 + r * Math.sin((startAngle * Math.PI) / 180);
          const x2 = 100 + r * Math.cos((endAngle * Math.PI) / 180);
          const y2 = 100 + r * Math.sin((endAngle * Math.PI) / 180);
          const largeArc = endAngle - startAngle > 180 ? 1 : 0;
          return <path key={i} d={`M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2}`} stroke={z.color} strokeWidth="14" fill="none" strokeLinecap="round" opacity={0.8} />;
        })}
        {/* Needle */}
        <line x1="100" y1="100" x2={100 + 60 * Math.cos((angle * Math.PI) / 180)} y2={100 + 60 * Math.sin((angle * Math.PI) / 180)} stroke="#f9fafb" strokeWidth="2.5" strokeLinecap="round" />
        <circle cx="100" cy="100" r="5" fill="#f9fafb" />
        <text x="100" y="95" textAnchor="middle" fill="#f9fafb" fontSize="22" fontWeight="700">{value != null ? value.toFixed(1) : '--'}{unit}</text>
      </svg>
      <div style={{ fontSize: '0.75rem', color: c.muted, marginTop: '-0.25rem' }}>{label}</div>
    </div>
  );
};

const BarMeter: React.FC<{ value: number; min: number; max: number; color: string; label: string; displayValue: string }> = ({ value, min, max, color, label, displayValue }) => {
  const pct = Math.max(0, Math.min(100, ((value - min) / (max - min)) * 100));
  return (
    <div style={{ marginBottom: '0.75rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', marginBottom: '0.3rem' }}>
        <span style={{ color: c.muted }}>{label}</span>
        <span style={{ fontWeight: 600 }}>{displayValue}</span>
      </div>
      <div style={{ background: c.cardAlt, borderRadius: 6, height: 10, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 6, transition: 'width 0.5s ease' }} />
      </div>
    </div>
  );
};

export const MarketPulseModal: React.FC<{ token: string; open: boolean; onClose: () => void }> = ({ token, open, onClose }) => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !token) return;
    setLoading(true); setError('');
    fetch(`${API}/spx/v2-technicals`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => { if (d.error && !d.price) setError(d.error); else setData(d); })
      .catch(() => setError('Failed to fetch market data'))
      .finally(() => setLoading(false));
  }, [open, token]);

  if (!open) return null;

  const rsi = data?.rsi14;
  const rsiColor = rsi != null ? (rsi < 30 ? c.green : rsi > 70 ? c.red : c.blue) : c.muted;
  const sentColor = data?.sentiment != null ? (data.sentiment > 0.1 ? c.green : data.sentiment < -0.1 ? c.red : c.amber) : c.muted;
  const changeColor = data?.change > 0 ? c.green : data?.change < 0 ? c.red : c.text;

  return (
    <>
      {/* Overlay */}
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 300 }} />

      {/* Modal */}
      <div style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        background: c.card, border: `1px solid ${c.border}`, borderRadius: 12,
        width: 520, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto',
        zIndex: 310, padding: '1.5rem', boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.2rem', color: c.text }}>Market Pulse</h2>
            <div style={{ fontSize: '0.7rem', color: c.dim }}>
              {data?.cached ? `Cached (${data.cacheAge}s ago)` : 'Live'} {data?.fetchedAt ? `| ${fmtDate(data.fetchedAt)}` : ''}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: c.muted, cursor: 'pointer', fontSize: '1.4rem' }}>&times;</button>
        </div>

        {loading && <div style={{ textAlign: 'center', padding: '2rem', color: c.muted }}>Fetching market data...</div>}
        {error && <div style={{ textAlign: 'center', padding: '2rem', color: c.red }}>{error}</div>}

        {data && !loading && (
          <>
            {/* Price header */}
            <div style={{ background: c.cardAlt, borderRadius: 8, padding: '1rem', marginBottom: '1rem', textAlign: 'center' }}>
              <div style={{ fontSize: '0.7rem', color: c.muted }}>SPY (S&P 500 Proxy)</div>
              <div style={{ fontSize: '2rem', fontWeight: 700, color: c.text }}>${data.price}</div>
              <div style={{ fontSize: '1rem', fontWeight: 600, color: changeColor }}>
                {data.change > 0 ? '+' : ''}{data.change} ({data.changePercent > 0 ? '+' : ''}{data.changePercent}%)
              </div>
              <div style={{ fontSize: '0.7rem', color: c.dim, marginTop: '0.25rem' }}>
                Day: ${data.dayLow} — ${data.dayHigh} | Prev Close: ${data.previousClose}
              </div>
            </div>

            {/* RSI Gauge + Sentiment side by side */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div style={{ background: c.cardAlt, borderRadius: 8, padding: '0.75rem' }}>
                <GaugeArc
                  value={rsi ?? 50}
                  min={0} max={100}
                  zones={[
                    { from: 0, to: 30, color: c.green },
                    { from: 30, to: 70, color: c.blue },
                    { from: 70, to: 100, color: c.red },
                  ]}
                  label={`RSI 14 — ${data.rsiZone === 'oversold' ? 'Oversold (potential bounce)' : data.rsiZone === 'overbought' ? 'Overbought (potential pullback)' : 'Neutral range'}`}
                />
              </div>
              <div style={{ background: c.cardAlt, borderRadius: 8, padding: '0.75rem' }}>
                <GaugeArc
                  value={data.sentiment != null ? (data.sentiment + 1) * 50 : 50}
                  min={0} max={100}
                  zones={[
                    { from: 0, to: 30, color: c.red },
                    { from: 30, to: 70, color: c.amber },
                    { from: 70, to: 100, color: c.green },
                  ]}
                  label={`News Sentiment — ${data.sentimentLabel}`}
                />
              </div>
            </div>

            {/* SMA + Volume bars */}
            <div style={{ background: c.cardAlt, borderRadius: 8, padding: '1rem', marginBottom: '1rem' }}>
              <div style={{ fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.75rem', color: c.text }}>Trend Indicators</div>
              <BarMeter
                value={data.sma20Distance ?? 0} min={-5} max={5}
                color={data.priceVsSma20 === 'above' ? c.green : c.red}
                label="Price vs 20-Day Average"
                displayValue={`${data.priceVsSma20 === 'above' ? '+' : ''}${data.sma20Distance ?? '?'}% ${data.priceVsSma20 || ''}`}
              />
              <BarMeter
                value={data.sma50Distance ?? 0} min={-5} max={5}
                color={data.priceVsSma50 === 'above' ? c.green : c.red}
                label="Price vs 50-Day Average"
                displayValue={`${data.priceVsSma50 === 'above' ? '+' : ''}${data.sma50Distance ?? '?'}% ${data.priceVsSma50 || ''}`}
              />
              <BarMeter
                value={data.volumeRatio ?? 1} min={0} max={3}
                color={data.volumeRatio > 1.5 ? c.amber : data.volumeRatio > 1 ? c.green : c.muted}
                label="Volume vs Average"
                displayValue={`${data.volumeRatio ?? '?'}x`}
              />
            </div>

            {/* Headlines */}
            {data.headlines?.length > 0 && (
              <div style={{ background: c.cardAlt, borderRadius: 8, padding: '1rem' }}>
                <div style={{ fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.5rem', color: c.text }}>Latest Headlines</div>
                {data.headlines.map((h: any, i: number) => (
                  <div key={i} style={{ fontSize: '0.78rem', color: c.muted, padding: '0.35rem 0', borderBottom: i < data.headlines.length - 1 ? `1px solid ${c.border}` : 'none' }}>
                    <span style={{ color: sentColor, fontWeight: 600, marginRight: '0.5rem' }}>{data.sentiment > 0.05 ? '+' : data.sentiment < -0.05 ? '-' : '~'}</span>
                    {h.title} <span style={{ color: c.dim, fontSize: '0.65rem' }}>— {h.publisher}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

// ── Main Component ──────────────────────────────────────────────────────────

export const SpxSignalsV2: React.FC = () => {
  const { user, token } = useAuth();
  const [signals, setSignals] = useState<any[]>([]);
  const [signal, setSignal] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pulseOpen, setPulseOpen] = useState(false);
  const [tab, setTab] = useState<'current' | 'history'>('current');
  const [histDate, setHistDate] = useState(() => new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' }));
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const [aiExpanded, setAiExpanded] = useState<Record<string, boolean>>({ claude: false, chatgpt: false });
  const [chatOpen, setChatOpen] = useState(false);
  const [prevDaySkew, setPrevDaySkew] = useState<any>(undefined);

  const load = useCallback(async (date?: string) => {
    if (!token) return;
    setLoading(true); setError('');
    try {
      // Always pass explicit date to get all snapshots (default API returns Limit:1)
      const today = new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
      const queryDate = date || today;
      let resp = await fetch(`${API}/spx/signals-v2?date=${queryDate}`, { headers: { Authorization: `Bearer ${token}` } });
      let data = await resp.json();
      let loadedDate = queryDate;
      if (!data.signals?.length && !date) {
        const yd = new Date(Date.now() - 86400000).toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
        resp = await fetch(`${API}/spx/signals-v2?date=${yd}`, { headers: { Authorization: `Bearer ${token}` } });
        data = await resp.json();
        loadedDate = yd;
      }
      setSignals(data.signals || []);
      if (data.signals?.length > 0) {
        setSignal(data.signals[0]);
        const exp: Record<number, boolean> = {};
        Object.keys(data.signals[0]?.horizons || {}).forEach(k => { exp[Number(k)] = false; });
        setExpanded(exp);

        // Fetch previous day's last skew for first-of-day comparison
        const signalDate = loadedDate || data.signals[0]?.date;
        if (signalDate) {
          try {
            const prevDate = new Date(signalDate + 'T12:00:00');
            prevDate.setDate(prevDate.getDate() - 1);
            const prevDateStr = prevDate.toISOString().slice(0, 10);
            const prevResp = await fetch(`${API}/spx/signals-v2?date=${prevDateStr}`, { headers: { Authorization: `Bearer ${token}` } });
            const prevData = await prevResp.json();
            if (prevData.signals?.length > 0) {
              setPrevDaySkew(prevData.signals[0]?.skew || null);
            } else {
              setPrevDaySkew(null);
            }
          } catch { setPrevDaySkew(null); }
        }
      } else setSignal(null);
    } catch { setError('Failed to load'); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { if (tab === 'current') load(); }, [tab, load]);

  const toggle = (t: number) => setExpanded(p => ({ ...p, [t]: !p[t] }));

  // Match AI pick to horizon + enrich with our model's full metrics
  const getAiPick = (analysis: any, targetDte: number, actualDte: number, horizonCandidates: any[]) => {
    if (!analysis) return null;

    // Search best_per_horizon — could be array or dict, AI may use target or actual DTE
    let pick: any = null;
    const bph = analysis.best_per_horizon;
    if (Array.isArray(bph)) {
      pick = bph.find((p: any) =>
        p.horizon_dte === targetDte || p.horizon_dte === actualDte ||
        p.dte === actualDte || p.dte === targetDte
      ) || null;
    } else if (bph && typeof bph === 'object') {
      pick = bph[String(targetDte)] || bph[String(actualDte)] || null;
    }

    // Fallback to top_picks
    if (!pick) {
      const picks = analysis.top_picks || [];
      pick = picks.find((p: any) =>
        p.horizon_dte === targetDte || p.horizon_dte === actualDte ||
        p.dte === actualDte || p.dte === targetDte
      ) || null;
    }

    if (!pick) return null;

    // Enrich with full metrics from our model's candidates (match by strike)
    const strike = Number(pick.strike);
    const match = (horizonCandidates || []).find((c: any) => Number(c.strike) === strike);
    if (match) {
      return { ...match, ...pick, reasoning: pick.reasoning };
    }
    return pick;
  };

  const horizons = signal?.horizons || {};
  const sorted = Object.keys(horizons).map(Number).sort((a, b) => a - b);
  const skew = signal?.skew;
  const skC = skewCfg[skew?.skew_regime] || skewCfg.normal;
  // Find previous snapshot's skew for change explanation (fall back to previous day's last run)
  const currentIdx = signals.findIndex((s: any) => s.snapshot_time === signal?.snapshot_time);
  const prevSnapshotSkew = currentIdx >= 0 && currentIdx < signals.length - 1 ? signals[currentIdx + 1]?.skew : undefined;
  const prevSkew = prevSnapshotSkew || (currentIdx === signals.length - 1 ? prevDaySkew : undefined);

  const tabS = (active: boolean): React.CSSProperties => ({
    padding: '0.6rem 1.25rem', cursor: 'pointer', border: 'none',
    borderBottom: active ? `2px solid ${c.purple}` : '2px solid transparent',
    background: 'transparent', color: active ? c.text : c.muted, fontWeight: active ? 600 : 400, fontSize: '0.9rem',
  });

  // Gate: Phase 5 - entitlement-first with legacy flat-flag fallback.
  if (!canAccessSymbol(user as any, 'SPX')) {
    return (
      <div className="dashboard-container">
        <Navigation />
        <div style={{ maxWidth: 700, margin: '0 auto', padding: '3rem 1.5rem', textAlign: 'center', color: '#f9fafb' }}>
          <h2 style={{ marginBottom: '0.75rem' }}>SPX V2 Time Horizons</h2>
          <p style={{ color: '#9ca3af', maxWidth: 500, margin: '0 auto 1.5rem' }}>
            AI-powered SPX put selling research across 8 time horizons with independent Claude + ChatGPT analysis, skew monitoring, and real-time scoring.
          </p>
          <a href="/upgrade" style={{ display: 'inline-block', backgroundColor: '#6d28d9', color: 'white', padding: '0.75rem 1.5rem', textDecoration: 'none', borderRadius: 8, fontWeight: 'bold', fontSize: 14 }}>
            SPX Signals — $790/yr
          </a>
          <p style={{ color: '#6b7280', fontSize: 13, marginTop: 12 }}>
            Or get everything with the <a href="/upgrade" style={{ color: '#a78bfa' }}>Bundle — $1,990/yr</a>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <Navigation />
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '1.5rem', color: c.text, fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <h1 style={{ margin: 0, fontSize: '1.5rem' }}>SPX Put Selling — V2 Time Horizons</h1>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button onClick={() => setPulseOpen(true)} style={{
              padding: '0.5rem 1rem', borderRadius: 6, border: 'none', cursor: 'pointer',
              fontWeight: 600, fontSize: '0.82rem', background: 'linear-gradient(135deg, #10b981, #059669)',
              color: '#fff', boxShadow: '0 2px 8px rgba(16,185,129,0.3)',
              display: 'flex', alignItems: 'center', gap: '0.4rem',
            }}>Market Pulse</button>
            <button onClick={() => load(tab === 'history' ? histDate : undefined)} style={{
              padding: '0.5rem 1rem', borderRadius: 6, border: `1px solid ${c.border}`,
              background: 'transparent', color: c.muted, cursor: 'pointer', fontSize: '0.82rem',
            }}>Refresh</button>
            {signal && (
              <button onClick={() => setChatOpen(!chatOpen)} style={{
                padding: '0.6rem 1.25rem', borderRadius: 8, border: 'none',
                background: chatOpen ? '#7c3aed' : 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                color: '#fff', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 700,
                boxShadow: chatOpen ? 'none' : '0 2px 8px rgba(139,92,246,0.4)',
                display: 'flex', alignItems: 'center', gap: '0.4rem',
              }}>
                <span style={{ fontSize: '1.1rem' }}>&#128172;</span>
                {chatOpen ? 'Close Chat' : 'Ask AI'}
              </button>
            )}
          </div>
        </div>
        {signal && (
          <div style={{ color: c.muted, fontSize: '0.85rem', marginBottom: '0.75rem' }}>
            Spot: {fmt(signal.spot_price)} | Snapshot: {fmtDate(signal.snapshot_time)}
          </div>
        )}

        {/* Tabs */}
        <div style={{ borderBottom: `1px solid ${c.border}`, marginBottom: '1rem', display: 'flex' }}>
          <button style={tabS(tab === 'current')} onClick={() => setTab('current')}>Current</button>
          <button style={tabS(tab === 'history')} onClick={() => setTab('history')}>History</button>
        </div>

        {/* History picker */}
        {tab === 'history' && !loading && (
          <div style={{ background: c.card, borderRadius: 8, border: `1px solid ${c.border}`, padding: '0.75rem', display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
            <label style={{ fontSize: '0.85rem', color: c.muted }}>Date:</label>
            <input type="date" value={histDate} onChange={e => setHistDate(e.target.value)} style={{
              background: c.cardAlt, color: c.text, border: `1px solid ${c.border}`, borderRadius: 4, padding: '0.4rem 0.6rem', fontSize: '0.85rem',
            }} />
            <button onClick={() => load(histDate)} style={{
              padding: '0.5rem 1rem', borderRadius: 6, border: 'none', background: c.purple, color: '#fff', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer',
            }}>Load</button>
          </div>
        )}

        {/* Snapshot selector */}
        {!loading && signals.length > 1 && (
          <div style={{ background: c.card, borderRadius: 8, border: `1px solid ${c.border}`, padding: '0.5rem 0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
            <span style={{ fontSize: '0.85rem', color: c.muted }}>Snapshot:</span>
            {signals.map(s => (
              <button key={s.snapshot_time} onClick={() => setSignal(s)} style={{
                padding: '0.35rem 0.75rem', borderRadius: 6, border: 'none', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer',
                background: signal?.snapshot_time === s.snapshot_time ? c.purple : c.border, color: '#fff',
              }}>{fmtDate(s.snapshot_time)}</button>
            ))}
          </div>
        )}

        {error && <div style={{ background: '#7f1d1d', border: `1px solid ${c.red}`, borderRadius: 6, padding: '0.75rem', marginBottom: '1rem', color: '#fca5a5', fontSize: '0.85rem' }}>{error}</div>}
        {loading && <div style={{ textAlign: 'center', padding: '3rem', color: c.muted }}>Loading research...</div>}
        {!loading && !signal && <div style={{ textAlign: 'center', padding: '3rem', color: c.muted }}>No research available yet. Readings are generated automatically during scheduled runs.</div>}

        {!loading && signal && (
          <>
            {/* Summary stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem', marginBottom: '1rem' }}>
              {[
                { label: 'Spot Price', value: fmt(signal.spot_price) },
                { label: 'Options Analyzed', value: String(signal.total_candidates) },
                { label: 'Time Horizons', value: String(sorted.length) },
                { label: 'Strategy', value: 'Income from Put Selling' },
              ].map(s => (
                <div key={s.label} style={{ background: c.card, borderRadius: 8, border: `1px solid ${c.border}`, padding: '0.75rem', textAlign: 'center' }}>
                  <div style={{ fontSize: '0.7rem', color: c.muted, marginBottom: '0.25rem' }}>{s.label}</div>
                  <div style={{ fontSize: '1.1rem', fontWeight: 700 }}>{s.value}</div>
                </div>
              ))}
            </div>

            {/* Skew / Fear Gauge */}
            {skew && (
              <SkewCard skew={skew} prevSkew={prevSkew} />
            )}

            {/* AI Summaries — collapsible, before DTEs */}
            {[
              { key: 'claude', analysis: signal.claude_analysis, label: 'Claude Independent Market View', color: c.blue },
              { key: 'chatgpt', analysis: signal.chatgpt_analysis, label: 'ChatGPT Independent Market View', color: '#10b981' },
            ].filter(a => a.analysis?.summary).map(({ key, analysis, label, color }) => (
              <div key={key} style={{ background: c.card, borderRadius: 8, border: `1px solid ${c.border}`, marginBottom: '0.75rem', overflow: 'hidden' }}>
                <div
                  onClick={() => setAiExpanded(p => ({ ...p, [key]: !p[key] }))}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem 1rem',
                    cursor: 'pointer', userSelect: 'none', borderLeft: `4px solid ${color}`,
                    background: aiExpanded[key] ? color + '10' : 'transparent',
                  }}
                >
                  <span style={{ fontSize: '0.7rem', color: c.muted, transform: aiExpanded[key] ? 'rotate(90deg)' : 'rotate(0)', transition: 'transform 0.2s', display: 'inline-block' }}>&#9654;</span>
                  <h4 style={{ margin: 0, fontSize: '0.95rem' }}>{label}</h4>
                  <span style={badge(color, color + '20')}>AI</span>
                  {!aiExpanded[key] && analysis.summary && (
                    <span style={{ color: c.dim, fontSize: '0.75rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                      {analysis.summary.substring(0, 120)}...
                    </span>
                  )}
                </div>
                {aiExpanded[key] && (
                  <div style={{ padding: '0.75rem 1rem', borderTop: `1px solid ${c.border}` }}>
                    {analysis.market_context && (
                      <div style={{ background: c.cardAlt, borderRadius: 6, padding: '0.6rem', marginBottom: '0.75rem', border: `1px solid ${c.border}` }}>
                        <div style={{ fontSize: '0.65rem', color: c.muted, fontWeight: 600, marginBottom: '0.15rem' }}>Market Context</div>
                        <p style={{ fontSize: '0.82rem', color: c.muted, lineHeight: 1.6, margin: 0 }}>{analysis.market_context}</p>
                      </div>
                    )}
                    <div style={{ marginBottom: '0.75rem' }}>
                      <div style={{ fontSize: '0.65rem', color: c.muted, fontWeight: 600, marginBottom: '0.15rem' }}>Analysis Summary</div>
                      <p style={{ fontSize: '0.82rem', lineHeight: 1.6, margin: 0 }}>{analysis.summary}</p>
                    </div>
                    {analysis.risk_warnings?.length > 0 && (
                      <div style={{ background: c.amberBg, border: `1px solid ${c.amber}40`, borderRadius: 6, padding: '0.6rem' }}>
                        <div style={{ fontSize: '0.65rem', color: c.amber, fontWeight: 600, marginBottom: '0.25rem' }}>Things to Watch</div>
                        <ul style={{ margin: 0, paddingLeft: '1rem', fontSize: '0.8rem', color: c.text }}>
                          {analysis.risk_warnings.map((w: string, i: number) => <li key={i} style={{ marginBottom: '0.2rem', lineHeight: 1.5 }}>{w}</li>)}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}

            {/* DTE Horizons */}
            {[0, 1, 7, 14, 21, 30, 45, 60, 90, 120].filter(target => horizons[target]).map(target => {
              const h = horizons[target];
              const cfg = HORIZON[target] || { label: `${target}d`, desc: '', color: c.blue };
              const isOpen = expanded[target];
              const isMatched = h?.matched !== false && h?.best;
              const ourBest = h?.best;
              const hCandidates = h?.candidates || [];
              const claudePick = getAiPick(signal.claude_analysis, target, h.actual_dte, hCandidates);
              const chatgptPick = getAiPick(signal.chatgpt_analysis, target, h.actual_dte, hCandidates);

              const convergence = convergenceStyle(signal.convergence?.[target]);

              const ourReasoning = ourBest ? `This is our top pick for the ${cfg.label} horizon because it offers strong income (${fmt(ourBest.prob_profit_pct, 1)}% estimated chance of profit) with a comfortable safety margin — the strike price sits ${fmt(ourBest.points_from_atm, 0)} points below the current market price. You would collect $${fmt(ourBest.credit_per_day, 3)} per day in premium, and the option expires in ${h.actual_dte} days. It scored highest in our analysis across safety, income, and efficiency.\n\nNote: We compare options that expire at the same time against each other to find the best within each horizon. The AI evaluates independently and may prefer a different strike. Both approaches are valuable — together they give you a more complete picture.` : '';

              return (
                <div key={target} style={{ marginBottom: '0.5rem' }}>
                  {/* Horizon header — collapsible */}
                  <div onClick={() => toggle(target)} style={{
                    background: c.card, borderRadius: isOpen ? '8px 8px 0 0' : 8, border: `1px solid ${c.border}`,
                    borderLeft: `4px solid ${cfg.color}`, padding: '0.75rem 1rem', cursor: 'pointer', userSelect: 'none',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <span style={{ fontSize: '0.7rem', color: c.muted, transform: isOpen ? 'rotate(90deg)' : 'rotate(0)', transition: 'transform 0.2s', display: 'inline-block' }}>&#9654;</span>
                      <span style={{ fontWeight: 700, fontSize: '1rem' }}>{cfg.label}</span>
                      <span style={{ color: c.muted, fontSize: '0.75rem' }}>{cfg.desc}</span>
                      <span style={{ color: c.dim, fontSize: '0.68rem' }}>({fmtExp(h.expiration)}, {h.count} candidates)</span>
                    </div>
                    {isMatched ? (
                      <div style={{ display: 'flex', gap: '1.25rem', fontSize: '0.78rem', alignItems: 'center' }}>
                        <span><span style={{ color: c.muted }}>Strike </span><span style={{ fontWeight: 700 }}>{ourBest?.strike}</span></span>
                        <span><span style={{ color: c.muted }}>Credit </span><span style={{ fontWeight: 700, color: c.green }}>${fmt(ourBest?.credit)}</span></span>
                        <span><span style={{ color: c.muted }}>Win </span><span style={{ fontWeight: 700 }}>{fmt(ourBest?.prob_profit_pct, 1)}%</span></span>
                        {convergence && (
                          <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: '0.68rem', fontWeight: 700, background: convergence.bg, color: convergence.color, letterSpacing: '0.02em' }}>
                            {convergence.label}
                          </span>
                        )}
                      </div>
                    ) : (
                      <span style={{ fontSize: '0.75rem', color: c.dim, fontStyle: 'italic' }}>Not available</span>
                    )}
                  </div>

                  {/* Expanded — 3 trade cards */}
                  {isOpen && (
                    <div style={{ background: c.cardAlt, borderRadius: '0 0 8px 8px', border: `1px solid ${c.border}`, borderTop: 'none', padding: '1rem' }}>
                      {isMatched ? (
                        <>
                          {convergence && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.6rem 0.85rem', marginBottom: '0.75rem', borderRadius: 6, background: convergence.bg, border: `1px solid ${convergence.color}30` }}>
                              <div>
                                <span style={{ fontWeight: 700, color: convergence.color, fontSize: '0.82rem' }}>{convergence.label}</span>
                                <span style={{ color: c.muted, fontSize: '0.75rem', marginLeft: 8 }}>
                                  Model: {convergence.modelStrike} | Claude: {convergence.claudeStrike ?? 'n/a'} | GPT: {convergence.gptStrike ?? 'n/a'}
                                  {convergence.modelStrike === convergence.claudeStrike && convergence.modelStrike === convergence.gptStrike
                                    ? ' — All three analyses pick the same strike.'
                                    : convergence.claudeStrike === convergence.gptStrike
                                    ? ` — Both AIs independently agree on ${convergence.claudeStrike}.`
                                    : convergence.modelStrike === convergence.claudeStrike || convergence.modelStrike === convergence.gptStrike
                                    ? ' — Model agrees with one AI; the other differs.'
                                    : ' — Each analysis recommends a different strike.'}
                                </span>
                              </div>
                            </div>
                          )}
                          {convergence && <ConvergenceHelp color={convergence.color} />}
                          <TradeCard title="Top Recommended Trade" badgeText="OUR TOP PICK" accentColor={cfg.color} trade={ourBest} reasoning={ourReasoning} />
                          <TradeCard title="Claude's Independent Pick" badgeText="INDEPENDENT AI" accentColor={c.blue} trade={claudePick} reasoning={claudePick?.reasoning} />
                          <TradeCard title="ChatGPT's Independent Pick" badgeText="INDEPENDENT AI" accentColor="#10b981" trade={chatgptPick} reasoning={chatgptPick?.reasoning} />
                        </>
                      ) : (
                        <div style={{ padding: '1.5rem', textAlign: 'center' }}>
                          <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem', opacity: 0.5 }}>&#8709;</div>
                          <p style={{ color: c.muted, fontSize: '0.85rem', lineHeight: 1.6, margin: 0, maxWidth: 500, marginLeft: 'auto', marginRight: 'auto' }}>
                            There are no specific candidates for this {target}-day horizon at this time. {h?.reason || 'This can happen when the market is closed, the expiration has passed, or no options meet our quality criteria for this timeframe.'}
                          </p>
                          {h?.nearest_dte > 0 && (
                            <p style={{ color: c.dim, fontSize: '0.78rem', marginTop: '0.5rem', fontStyle: 'italic' }}>
                              The nearest available expiration is {h.nearest_dte} DTE ({fmtExp(h.expiration)}).
                              Check the {h.nearest_dte}-day horizon above for the closest alternative.
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

          </>
        )}
      </div>

      {/* Chat sidebar — rendered outside main content */}
      {signal && token && (
        <ChatSidebar signal={signal} token={token} open={chatOpen} onClose={() => setChatOpen(false)} />
      )}
      {token && (
        <MarketPulseModal token={token} open={pulseOpen} onClose={() => setPulseOpen(false)} />
      )}
    </div>
  );
};
